import React, { useContext, useMemo } from 'react';
import { Progress } from 'antd';
import { LangContext } from '../../contexts/langContext';
import { StateContext } from '../../contexts/stateContext';
import { yoni_dictionary } from '../../static/jyotish_local';
import {
  getGanaFromChartData,
  getGanaCompatibility,
  getVarnaFromChartData,
  getVarnaCompatibility,
  getYoniCompatibility,
  getYoniIdFromKundli,
} from '../../static/jyotish_utils';
import { JyotishGenderToggle } from './JyotishGenderToggle';

const resolveYoni = (yoniId) => {
  if (!yoniId) return null;
  return yoni_dictionary[yoniId]
    || Object.values(yoni_dictionary).find((y) => y.id === yoniId)
    || null;
};

const STATUS_ICON = {
  match: '✓',
  mismatch: '✗',
  neutral: '·',
};

const PROGRESS_COLORS = {
  match: '#3fb56b',
  mismatch: '#cf5b5b',
  neutral: '#d9a441',
};

const PROGRESS_SECTIONS = ['yoni', 'gana'];

/**
 * Centered compatibility strip for a 2-person penta row (yoni / varna / gana).
 */
export function JyotishCompat({
  section,
  kundliA,
  kundliB,
  genderA,
  genderB,
  onGenderAChange,
  onGenderBChange,
}) {
  const context = { ...useContext(LangContext), ...useContext(StateContext) };
  const $ui = context.$ui;
  const language = context.language;
  const varnaBy = context.state?.varnaBy ?? 'nakshatra';

  const result = useMemo(() => {
    if (!kundliA || !kundliB) return null;

    if (section === 'yoni') {
      const idA = getYoniIdFromKundli(kundliA);
      const idB = getYoniIdFromKundli(kundliB);
      const compat = getYoniCompatibility(idA, idB, genderA, genderB);
      if (!compat) return null;
      const yA = resolveYoni(idA);
      const yB = resolveYoni(idB);
      const titleA = yA?.[`title-${language}`] || yA?.id || idA;
      const titleB = yB?.[`title-${language}`] || yB?.id || idB;
      return {
        ...compat,
        label: `${yA?.emoji || ''} ${titleA} × ${yB?.emoji || ''} ${titleB}`.trim(),
      };
    }

    if (section === 'varna') {
      const vA = getVarnaFromChartData(kundliA, varnaBy);
      const vB = getVarnaFromChartData(kundliB, varnaBy);
      const compat = getVarnaCompatibility(vA.varna, vB.varna, genderA, genderB);
      if (!compat) return null;
      return {
        ...compat,
        label: `${vA.varna} × ${vB.varna}`,
      };
    }

    if (section === 'gana') {
      const gA = getGanaFromChartData(kundliA);
      const gB = getGanaFromChartData(kundliB);
      const compat = getGanaCompatibility(gA.gana, gB.gana, genderA, genderB);
      if (!compat) return null;
      return {
        ...compat,
        label: `${gA.gana} × ${gB.gana}`,
      };
    }

    return null;
  }, [section, kundliA, kundliB, genderA, genderB, varnaBy, language]);

  if (!result) return null;

  const statusKey = `jyotish_compat_${result.status}`;
  const scoreText = typeof result.score === 'number' && result.maxScore
    ? `${result.score}/${result.maxScore}`
    : null;

  const showProgress = PROGRESS_SECTIONS.includes(section)
    && typeof result.score === 'number'
    && result.maxScore;
  const progressPercent = showProgress
    ? Math.round((result.score / result.maxScore) * 100)
    : null;

  return (
    <div className={`x_penta_compat ${result.status ? `x_penta_compat--${result.status}` : ''}`}>

      <span className="x_penta_compat_line x_penta_compat_line--1"></span>

      <div className={`x_penta_compat_inner x_penta_compat_inner--${result.status}`}>
        {showProgress ? (
          <div className="x_penta_compat_progress">
            <span className="x_penta_compat_label">
              {$ui('jyotish_compat_title')}: <b>{$ui(statusKey)}</b>
              <span className="x_penta_compat_score_wrapper">
              {scoreText ? <span className="x_penta_compat_score">{scoreText}</span> : null}
              &nbsp;&nbsp;
              <Progress
                percent={progressPercent}
                steps={result.maxScore}
                size="small"
                showInfo={false}
                strokeColor={PROGRESS_COLORS[result.status] || PROGRESS_COLORS.neutral}
              />
              </span>
            </span>
          </div>
        ) : (
          <>
            {/* <span className="x_penta_compat_icon">{STATUS_ICON[result.status]}</span> */}
            <span className="x_penta_compat_label">
              {$ui('jyotish_compat_title')}: <b>{$ui(statusKey)}</b>
            </span>
          </>
        )}
        {result.needsGender && (<>
          <span className="x_penta_compat_hint">{$ui('jyotish_compat_needs_gender')}</span>
          <div className="x_penta_compat_gender_toggles">
            <div className="_f _fl _fgrow">
              <JyotishGenderToggle gender={genderA} onChange={onGenderAChange} />
            </div>
            <div className="_f _fr _fgrow">
              <JyotishGenderToggle gender={genderB} onChange={onGenderBChange} />
            </div>
          </div>
        </>
        )}
      </div>

      <span className="x_penta_compat_line x_penta_compat_line--2"></span>

    </div>
  );
}
